// DraftViewBlockPickerModal.jsx

import React, { useMemo } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import AddViewCardModalOption from 'dashboard/modals/option/AddViewCardModalOption';
import { buildViewOptions } from 'views/modal/viewModalViewTypes';

const VIEW_TYPE_META = buildViewOptions(true).reduce((map, option) => {
  // Keep the first TIMELINE option (Timeline) for type-keyed lookups; Gantt shares the same type key.
  if (map[option.key]) return map;
  map[option.key] = {
    iconKey: option.iconKey,
    label: option.label,
  };
  return map;
}, {});

function normalizeViewType(viewType) {
  return String(viewType || 'LIST').trim().toUpperCase() || 'LIST';
}

/**
 * DraftViewBlockPickerModal
 *
 * Picker for assigning a draft view definition to a layout view block.
 *
 * @returns {ReactNode} The rendered component.
 */
const DraftViewBlockPickerModal = ({
  databaseLabel = 'this collection',
  draftViews = [],
  isOpen = false,
  onClose,
  onSelect,
  relatedTypeKey = '',
  title = 'Add View',
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const resolvedViews = useMemo(() => (
    (Array.isArray(draftViews) ? draftViews : [])
      .map((view) => {
        const key = String(view?.key || '').trim();
        if (!key) return null;

        const viewTitle = String(view?.title || 'Untitled View').trim() || 'Untitled View';
        const viewType = normalizeViewType(view?.type);
        const typeMeta = VIEW_TYPE_META[viewType] || VIEW_TYPE_META.LIST;

        return {
          description: String(view?.description || '').trim(),
          iconKey: typeMeta.iconKey,
          key,
          title: viewTitle,
          typeLabel: typeMeta.label,
          viewType,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.title.localeCompare(right.title))
  ), [draftViews]);

  const emptyStateSubtitle = `Add views under ${databaseLabel} → Views in the explorer, then return here to place one on the layout.`;

  // ----- HANDLERS ----- //

  /**
   * handleSelect
   *
   * Handles a selection change.
   *
   * @param {unknown} viewOption - viewOption.
   */
  const handleSelect = (viewOption) => {
    if (!viewOption?.key) return;

    onSelect?.({
      relatedTypeKey,
      titleOverride: viewOption.title,
      viewId: viewOption.key,
    });
    onClose?.();
  };

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="view_kanban"
      isOpen={isOpen}
      title={title}
      width={isMobile ? '100%' : 520}
      onClose={onClose}
    >
      <ModalForm>
        {!resolvedViews.length ? (
          <div
            className="draft-view-block-picker-empty"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            {emptyStateSubtitle}
          </div>
        ) : (
          <div className="draft-view-block-picker-options">
            {resolvedViews.map((viewOption) => (
              <AddViewCardModalOption
                key={viewOption.key}
                iconKey={viewOption.iconKey}
                note={viewOption.description || `${viewOption.typeLabel} view`}
                title={viewOption.title}
                onClick={() => handleSelect(viewOption)}
              />
            ))}
          </div>
        )}
      </ModalForm>
    </Modal>
  );
};

// Dev Tools
DraftViewBlockPickerModal.displayName = 'DraftViewBlockPickerModal';

export default DraftViewBlockPickerModal;
